import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { BufferLoading, ComposerInput, QueueItems } from '~/shared-components';
import ComposerPopover from '~/composer-popover';

import getErrorBoundary from '~/web/components/ErrorBoundary';
import ProfilesDisconnectedBanner from '~/profiles-disconnected-banner';

import Empty from '../Empty';
import ApprovalsUpsell from '../ApprovalsUpsell';

const ErrorBoundary = getErrorBoundary(true);

const composerStyle = {
  marginBottom: '1.5rem',
  flexGrow: '1',
};

const topBarContainerStyle = {
  display: 'flex',
};

const loadingContainerStyle = {
  width: '100%',
  height: '100%',
  textAlign: 'center',
  paddingTop: '5rem',
};

const containerStyle = {
  marginRight: '0.5rem',
};

export const showPaywall = (tabId) => {
  const hasApprovalEntitlement = useSelector(
    (state) => state.organizations.selected.hasApprovalFeature
  );
  const showPaywallApproval =
    !hasApprovalEntitlement &&
    (tabId === 'awaitingApproval' || tabId === 'pendingApproval');

  return showPaywallApproval;
};

const DraftList = ({
  loading,
  postLists,
  manager,
  onApproveClick,
  onDeleteConfirmClick,
  onEditClick,
  onRevertApprovalClick,
  onRequestApprovalClick,
  onRescheduleClick,
  onComposerPlaceholderClick,
  onComposerCreateSuccess,
  showComposer,
  editMode,
  tabId,
  isDisconnectedProfile,
  hasFirstCommentFlip,
  onConfirmDismissComposer,
  shouldResetComposerData,
  fetchDrafts,
  profileId,
}) => {
  // Fetch the Data
  useEffect(() => {
    const needsApproval = tabId !== 'drafts';
    fetchDrafts({ needsApproval });
  }, [tabId, profileId]);

  if (showPaywall(tabId)) {
    return <ApprovalsUpsell />;
  }

  if (loading) {
    return (
      <div style={loadingContainerStyle}>
        <BufferLoading size={64} />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      {isDisconnectedProfile && <ProfilesDisconnectedBanner />}
      <div className={containerStyle}>
        <div style={topBarContainerStyle}>
          {tabId === 'drafts' && (
            <div style={composerStyle}>
              {showComposer && !editMode && (
                <ComposerPopover
                  type="drafts"
                  onSave={onComposerCreateSuccess}
                  shouldResetComposerData={shouldResetComposerData}
                  onConfirmDismissComposer={onConfirmDismissComposer}
                />
              )}
              <ComposerInput
                placeholder="Create a new draft..."
                onPlaceholderClick={onComposerPlaceholderClick}
              />
            </div>
          )}
        </div>
        {showComposer && editMode && (
          <ComposerPopover
            type="drafts"
            onSave={onComposerCreateSuccess}
            onConfirmDismissComposer={onConfirmDismissComposer}
          />
        )}
        {postLists.length > 0 ? (
          <QueueItems
            items={postLists}
            onApproveClick={onApproveClick}
            onDeleteConfirmClick={onDeleteConfirmClick}
            onEditClick={onEditClick}
            onRevertApprovalClick={onRevertApprovalClick}
            onRequestApprovalClick={onRequestApprovalClick}
            onRescheduleClick={onRescheduleClick}
            draggable={false}
            type="drafts"
            hasFirstCommentFlip={hasFirstCommentFlip}
          />
        ) : (
          !isDisconnectedProfile && <Empty isManager={manager} view={tabId} />
        )}
      </div>
    </ErrorBoundary>
  );
};

DraftList.propTypes = {
  loading: PropTypes.bool,
  postLists: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
    })
  ),
  manager: PropTypes.bool.isRequired,
  onApproveClick: PropTypes.func.isRequired,
  onDeleteConfirmClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onRevertApprovalClick: PropTypes.func.isRequired,
  onRequestApprovalClick: PropTypes.func.isRequired,
  onRescheduleClick: PropTypes.func.isRequired,
  onComposerPlaceholderClick: PropTypes.func.isRequired,
  onComposerCreateSuccess: PropTypes.func.isRequired,
  showComposer: PropTypes.bool,
  editMode: PropTypes.bool,
  tabId: PropTypes.oneOf(['awaitingApproval', 'pendingApproval', 'drafts']),
  isDisconnectedProfile: PropTypes.bool,
  hasFirstCommentFlip: PropTypes.bool,
  onConfirmDismissComposer: PropTypes.func.isRequired,
  shouldResetComposerData: PropTypes.bool,
  fetchDrafts: PropTypes.func.isRequired,
  profileId: PropTypes.string.isRequired,
};

DraftList.defaultProps = {
  shouldResetComposerData: true,
  loading: true,
  postLists: [],
  showComposer: false,
  editMode: false,
  tabId: null,
  isDisconnectedProfile: false,
  hasFirstCommentFlip: false,
};

export default DraftList;
